import recipeTemplate from "../templates/pages/recipe.html?raw";
import NavBar from "../sections/navbar";
import { deleteApi, getApi, postApi } from "../services/api";
import { getStringFromLocalStorage, saveJsonToLocalStorage } from "../services/localStorage";
import { Page } from "./base";
import { getElementById, reloadWindow, setError, setLoading } from "../services/dom";
import { IRecipe, IRecipeBook } from "../types/recipe";
import Footer from "../sections/footer";
const cross = "/img/cross.svg";
const edit = "/img/edit.svg";
const tick = "/img/tick.svg";
import { S3UploadService } from "../services/s3Upload";

const apiUrl = import.meta.env.VITE_API_URL as string;
const apiToken = getStringFromLocalStorage("idToken");

export default class RecipePage extends Page {
  private selectedRecipeIngredients: any;
  // private newRecipeIngredientOptions: { ingredient_name?: string, unit_name?: string, quantity?: number } = {};
  private s3UploadService: S3UploadService;
  constructor() {
    super(recipeTemplate);
    this.s3UploadService = new S3UploadService();
    this.loadPage();
  }

  private async loadPage(): Promise<void> {
    // Create navbar and footer immediately (don't wait for stores)
    new NavBar();
    new Footer();
    this.defineListeners();
    super.handleCustomAttributes();
    this.handleDataModels();

    // Initialize stores in background, then load data
    await super.init();
    await this.loadData();
  }

  private defineListeners(): void {
    getElementById("createRecipeButton").addEventListener("click", () => {
      this.showRecipeModal(null);
    });
  }

  private async loadData(): Promise<void> {
    // Get Household Recipes before loading the Recipe Edit Section - force refresh from API
    await this.recipeStore.getHouseholdRecipes(true);
    // Get empty recipes to determine which ones to highlight
    await this.recipeStore.getEmptyRecipes();
    console.log("RecipePage: After getEmptyRecipes, emptyRecipes:", this.recipeStore.emptyRecipes);
    await this.loadRecipeEditSection();
  }

  private handleDataModels(): void {
    // These data models are now handled in the modal
    // No need to set up listeners for elements that don't exist in the main page
  }

  private async handlePhotoScan() {
    const photoUpload = getElementById("photoUpload") as HTMLInputElement;

    // Add event listener for file selection
    photoUpload.onchange = async (event: any) => {
      const file = event.target.files?.[0];
      if (file) {
        const scanButton = getElementById('scan-photo-btn') as HTMLButtonElement;
        const originalText = scanButton?.innerHTML || '';

        try {
          // Show loading spinner in button
          if (scanButton) {
            scanButton.disabled = true;
            scanButton.innerHTML = '<img src="/img/bars-loading.svg" class="w-6 h-6 inline" alt="Loading" /> Analyzing...';
          }

          // Step 1: Upload the file to S3
          this.message.timedMessage(`Uploading photo: ${file.name}...`);
          const s3Key = await this.s3UploadService.uploadFile(file);

          console.log("Photo uploaded to S3 with key:", s3Key);
          this.message.timedMessage(`Photo uploaded successfully. Extracting recipe...`);

          // Step 2: Extract recipe text using Textract
          const recipeData = await this.s3UploadService.extractRecipeText(s3Key);

          console.log("Recipe extraction completed:", recipeData);
          this.message.timedMessage(`Recipe extracted successfully!`);

          // Step 3: Populate the modal with extracted data
          this.populateModalWithData(recipeData.recipe_data);

        } catch (error) {
          console.error("Photo scan failed:", error);
          this.message.timedMessage(`⚠ Photo scan failed: ${(error as Error).message || 'Unknown error'} ⚠`);
        } finally {
          // Restore button state
          if (scanButton) {
            scanButton.disabled = false;
            scanButton.innerHTML = originalText;
          }
          // Reset file input
          photoUpload.value = '';
        }
      }
    };

    // Trigger the file picker
    photoUpload.click();
  }

  private populateModalWithData(recipeData: any) {
    // Update recipe name
    const recipeNameInput = getElementById('modal-recipe-name') as HTMLInputElement;
    if (recipeNameInput && recipeData.recipe_name) {
      recipeNameInput.value = recipeData.recipe_name;
    }

    // Update servings
    const servingsInput = getElementById('modal-servings') as HTMLInputElement;
    if (recipeData.servings) {
      const servings = this.extractServingsNumber(recipeData.servings);
      if (servings && servingsInput) {
        servingsInput.value = servings.toString();
      }
    }

    // Update ingredients list
    const ingredientsList = getElementById('ingredients-list');
    if (ingredientsList && recipeData.ingredients) {
      ingredientsList.innerHTML = this.generateIngredientsHTML(recipeData.ingredients);
    }
  }

  private showRecipeModal(recipeData: any | null) {
    // Remove any existing modal first
    const existingModal = document.getElementById('recipe-modal');
    if (existingModal) {
      existingModal.remove();
    }

    // Create modal overlay
    const modalOverlay = document.createElement('div');
    modalOverlay.id = 'recipe-modal';
    modalOverlay.className = 'fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4';

    // Extract default servings from the text or use default
    const defaultServings = recipeData ? (this.extractServingsNumber(recipeData.servings) || 4) : 4;
    const recipeName = recipeData?.recipe_name || '';
    const ingredients = recipeData?.ingredients || [];

    const modalHTML = `
      <div class="bg-gray-800 rounded-lg shadow-2xl max-w-5xl max-h-[95vh] overflow-hidden m-auto w-full flex flex-col text-white border border-gray-700">
        <div class="p-4 border-b border-gray-700 bg-gray-800">
          <div class="flex justify-between items-center">
            <div>
              <h2 class="text-xl font-bold text-white">Create New Recipe</h2>
              <p class="text-xs text-gray-400 mt-1">Enter recipe details manually or scan a photo to auto-fill</p>
            </div>
            <button id="close-modal" class="text-gray-400 hover:text-white text-3xl leading-none">&times;</button>
          </div>
        </div>

        <div class="flex-1 overflow-y-auto p-4 space-y-4">
          <!-- Scan Photo Button -->
          <div class="flex justify-center">
            <button id="scan-photo-btn" class="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center space-x-2">
              <span>📷</span>
              <span>Scan Photo</span>
            </button>
          </div>

          <!-- Recipe Basic Info -->
          <div class="bg-gray-700 p-3 rounded-lg">
            <h3 class="text-sm font-semibold text-white mb-3">Basic Information</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label class="block text-xs font-medium text-gray-300 mb-1">Recipe Name *</label>
                <input type="text" id="modal-recipe-name" value="${recipeName}"
                       placeholder="Enter recipe name..."
                       class="w-full px-3 py-2 border border-gray-600 rounded bg-gray-600 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
              </div>
              <div>
                <label class="block text-xs font-medium text-gray-300 mb-1">Number of People *</label>
                <input type="number" id="modal-servings" value="${defaultServings}" min="1" max="50"
                       placeholder="4"
                       class="w-full px-3 py-2 border border-gray-600 rounded bg-gray-600 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
              </div>
            </div>
          </div>

          <!-- Ingredients Section -->
          <div class="bg-gray-700 border border-gray-600 p-3 rounded-lg">
            <div class="flex justify-between items-center mb-3">
              <h3 class="text-sm font-semibold text-white">Ingredients</h3>
              <button id="add-ingredient-btn" class="bg-gray-600 hover:bg-gray-500 text-white text-xs px-2 py-1 rounded transition-colors border border-gray-500">
                + Add Ingredient
              </button>
            </div>
            <div id="ingredients-list" class="space-y-2 max-h-80 overflow-y-auto">
              ${this.generateIngredientsHTML(ingredients)}
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="p-4 border-t border-gray-700 bg-gray-800 flex justify-between items-center">
          <div class="flex items-center space-x-3">
            <button id="cancel-modal" class="px-3 py-2 text-sm text-gray-300 hover:text-white border border-gray-600 rounded hover:bg-gray-700 transition-colors">
              Cancel
            </button>
          </div>
          <div>
            <button id="save-recipe" class="px-6 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors font-medium">
              Add Recipe & Ingredients
            </button>
          </div>
        </div>
      </div>
    `;

    modalOverlay.innerHTML = modalHTML;
    document.body.appendChild(modalOverlay);

    // Add event listeners
    this.setupModalEventListeners(modalOverlay);
  }

  private extractServingsNumber(servingsText: string | number): number | null {
    if (!servingsText && servingsText !== 0) return null;

    // If already a number, return it
    if (typeof servingsText === 'number') {
      return servingsText;
    }

    // Convert to string and extract numbers from servings text like "4 people", "serves 6", "24 cookies", etc.
    const match = String(servingsText).match(/(\d+)/);
    return match ? parseInt(match[1]) : null;
  }

  private generateIngredientsHTML(ingredients: any[]): string {
    if (!ingredients || ingredients.length === 0) {
      return '<div id="no-ingredients" class="text-gray-400 italic p-3 text-center border-2 border-dashed border-gray-600 rounded-lg text-sm">No ingredients detected. Click "Add Ingredient" to add manually.</div>';
    }

    // Get ingredient and unit options from stores
    const ingredientOptions = this.ingredientStore.ingredients
      .map(ing => `<option value="${ing.name}">`)
      .join('');

    return ingredients.map((ingredient, index) => {
      // Extract structured data
      const amount = ingredient.amount || '';
      const unit = ingredient.unit || '';
      const name = ingredient.name || '';
      const isMatched = ingredient.matched !== false; // True if matched or undefined

      return `
        <div class="ingredient-row p-3 bg-gray-600 rounded border ${isMatched ? 'border-green-600' : 'border-yellow-600'}">
          <div class="flex items-start space-x-2">
            <div class="flex-shrink-0 w-6 h-6 ${isMatched ? 'bg-green-600' : 'bg-yellow-600'} text-white rounded-full flex items-center justify-center text-xs font-medium mt-1">
              ${index + 1}
            </div>
            <div class="flex-1 grid grid-cols-1 md:grid-cols-3 gap-2">
              <!-- Ingredient Name Field -->
              <div>
                <label class="block text-xs font-medium text-gray-300 mb-1">Ingredient Name</label>
                <input list="ingredients-list-${index}"
                       value="${name}"
                       class="w-full px-2 py-1.5 border border-gray-500 rounded bg-gray-700 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                       data-ingredient-index="${index}"
                       data-field="name"
                       placeholder="e.g., flour, sugar">
                <datalist id="ingredients-list-${index}">
                  ${ingredientOptions}
                </datalist>
              </div>

              <!-- Unit Field -->
              <div>
                <label class="block text-xs font-medium text-gray-300 mb-1">Unit</label>
                <select class="w-full px-2 py-1.5 border border-gray-500 rounded bg-gray-700 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        data-ingredient-index="${index}"
                        data-field="unit">
                  <option value="">-- Select Unit --</option>
                  ${this.unitStore.units.map(u => `<option value="${u.name}" ${u.name === unit ? 'selected' : ''}>${u.name}</option>`).join('')}
                </select>
              </div>

              <!-- Amount Field -->
              <div>
                <label class="block text-xs font-medium text-gray-300 mb-1">Amount</label>
                <input type="text"
                       value="${amount}"
                       class="w-full px-2 py-1.5 border border-gray-500 rounded bg-gray-700 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                       data-ingredient-index="${index}"
                       data-field="amount"
                       placeholder="e.g., 225, 1/2"
                       inputmode="decimal">
              </div>
            </div>
            <button class="remove-ingredient text-red-400 hover:text-red-300 text-sm px-2 py-1 rounded hover:bg-gray-700 transition-colors flex-shrink-0 mt-5"
                    data-ingredient-index="${index}">
              ✕
            </button>
          </div>
          ${!isMatched ? '<div class="text-xs text-yellow-400 mt-2 ml-8">⚠ Ingredient not matched automatically</div>' : ''}
        </div>
      `;
    }).join('');
  }

  private setupModalEventListeners(modalOverlay: HTMLElement) {
    // Close modal handlers
    const closeModal = () => modalOverlay.remove();

    getElementById('close-modal').addEventListener('click', closeModal);
    getElementById('cancel-modal').addEventListener('click', closeModal);

    // Click outside modal to close
    modalOverlay.addEventListener('click', (e) => {
      if (e.target === modalOverlay) closeModal();
    });

    // Scan photo button
    getElementById('scan-photo-btn').addEventListener('click', () => {
      this.triggerPhotoScan();
    });

    // Add ingredient functionality
    getElementById('add-ingredient-btn').addEventListener('click', () => {
      this.addIngredientRow();
    });

    // Remove ingredient functionality (event delegation)
    getElementById('ingredients-list').addEventListener('click', (e: any) => {
      if (e.target.classList.contains('remove-ingredient')) {
        e.target.parentElement.parentElement.remove();
        this.updateNoIngredientsMessage();
      }
    });

    // Save recipe functionality
    getElementById('save-recipe').addEventListener('click', () => {
      this.saveExtractedRecipe(modalOverlay);
    });
  }

  private triggerPhotoScan() {
    this.handlePhotoScan();
  }

  private addIngredientRow() {
    const ingredientsList = getElementById('ingredients-list');
    const noIngredientsMsg = getElementById('no-ingredients');

    if (noIngredientsMsg) {
      noIngredientsMsg.remove();
    }

    // Get current ingredient count for numbering
    const currentRows = ingredientsList.querySelectorAll('.ingredient-row');
    const nextNumber = currentRows.length + 1;
    const index = currentRows.length;

    // Get ingredient and unit options from stores
    const ingredientOptions = this.ingredientStore.ingredients
      .map(ing => `<option value="${ing.name}">`)
      .join('');

    const newRow = document.createElement('div');
    newRow.className = 'ingredient-row p-3 bg-gray-600 rounded border border-gray-500';
    newRow.innerHTML = `
      <div class="flex items-start space-x-2">
        <div class="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-medium mt-1">
          ${nextNumber}
        </div>
        <div class="flex-1 grid grid-cols-1 md:grid-cols-3 gap-2">
          <!-- Ingredient Name Field -->
          <div>
            <label class="block text-xs font-medium text-gray-300 mb-1">Ingredient Name</label>
            <input list="ingredients-list-${index}"
                   class="w-full px-2 py-1.5 border border-gray-500 rounded bg-gray-700 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                   data-ingredient-index="${index}"
                   data-field="name"
                   placeholder="e.g., flour, sugar">
            <datalist id="ingredients-list-${index}">
              ${ingredientOptions}
            </datalist>
          </div>

          <!-- Unit Field -->
          <div>
            <label class="block text-xs font-medium text-gray-300 mb-1">Unit</label>
            <select class="w-full px-2 py-1.5 border border-gray-500 rounded bg-gray-700 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    data-ingredient-index="${index}"
                    data-field="unit">
              <option value="">-- Select Unit --</option>
              ${this.unitStore.units.map(u => `<option value="${u.name}">${u.name}</option>`).join('')}
            </select>
          </div>

          <!-- Amount Field -->
          <div>
            <label class="block text-xs font-medium text-gray-300 mb-1">Amount</label>
            <input type="text"
                   class="w-full px-2 py-1.5 border border-gray-500 rounded bg-gray-700 text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                   data-ingredient-index="${index}"
                   data-field="amount"
                   placeholder="e.g., 225, 1/2"
                   inputmode="decimal">
          </div>
        </div>
        <button class="remove-ingredient text-red-400 hover:text-red-300 text-sm px-2 py-1 rounded hover:bg-gray-700 transition-colors flex-shrink-0 mt-5">
          ✕
        </button>
      </div>
    `;

    ingredientsList.appendChild(newRow);

    // Focus on the first input (amount)
    const newInput = newRow.querySelector('input');
    newInput?.focus();

    // Scroll to the new ingredient if the list is scrollable
    newRow.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  private updateNoIngredientsMessage() {
    const ingredientsList = getElementById('ingredients-list');
    const rows = ingredientsList.querySelectorAll('.ingredient-row');

    if (rows.length === 0) {
      ingredientsList.innerHTML = '<div id="no-ingredients" class="text-gray-400 italic p-3 text-center border-2 border-dashed border-gray-600 rounded-lg text-sm">No ingredients detected. Click "Add Ingredient" to add manually.</div>';
    }
  }

  private async saveExtractedRecipe(modalOverlay: HTMLElement) {
    const saveButton = getElementById('save-recipe') as HTMLButtonElement;
    const originalText = saveButton.textContent;

    try {
      saveButton.disabled = true;
      saveButton.textContent = 'Saving...';

      // Get form data
      const recipeName = (getElementById('modal-recipe-name') as HTMLInputElement).value.trim();
      const servings = parseInt((getElementById('modal-servings') as HTMLInputElement).value);

      if (!recipeName || !servings) {
        this.message.timedMessage('⚠ Recipe name and number of people are required! ⚠');
        return;
      }

      // Get ingredients from the three-field structure
      const ingredientRows = document.querySelectorAll('#ingredients-list .ingredient-row');
      const ingredients = Array.from(ingredientRows).map((row: Element) => {
        const amountInput = row.querySelector('[data-field="amount"]') as HTMLInputElement;
        const unitInput = row.querySelector('[data-field="unit"]') as HTMLInputElement;
        const nameInput = row.querySelector('[data-field="name"]') as HTMLInputElement;

        const amount = amountInput?.value.trim() || '';
        const unit = unitInput?.value.trim() || '';
        const name = nameInput?.value.trim() || '';

        // Return combined string format for now (amount unit name)
        return `${amount} ${unit} ${name}`.trim();
      }).filter(ingredient => ingredient.length > 0);

      // Step 1: Add the recipe
      await this.recipeStore.addRecipe(recipeName, servings);
      this.message.timedMessage(`Recipe '${recipeName}' created successfully!`);

      // Step 2: Add ingredients (if any)
      if (ingredients.length > 0) {
        this.message.timedMessage(`Adding ${ingredients.length} ingredients...`);
        await this.addIngredientsToRecipe(recipeName, ingredients, servings);
      }

      this.message.timedMessage(`✓ Recipe '${recipeName}' saved with ${ingredients.length} ingredients!`);

      // Close modal and refresh
      modalOverlay.remove();
      reloadWindow();

    } catch (error) {
      console.error('Error saving recipe:', error);
      this.message.timedMessage(`⚠ Error saving recipe: ${error || 'Unknown error'} ⚠`);
    } finally {
      saveButton.disabled = false;
      saveButton.textContent = originalText;
    }
  }

  private async addIngredientsToRecipe(recipeName: string, ingredients: string[], servings: number) {
    const failedIngredients: string[] = [];

    for (const ingredient of ingredients) {
      try {
        // Parse ingredient text to extract quantity, unit, and name
        const parsed = this.parseIngredientText(ingredient);

        await postApi(apiUrl, "recipeingredient", {
          recipe_name: recipeName,
          ingredient_name: parsed.name,
          quantity_per_person: parsed.quantity / servings,
          unit_name: parsed.unit,
        }, apiToken);

        console.log(`Added ingredient: ${parsed.name}`);
      } catch (error) {
        console.error(`Failed to add ingredient: ${ingredient}`, error);
        failedIngredients.push(ingredient);
      }
    }

    if (failedIngredients.length > 0) {
      this.message.timedMessage(`⚠ ${failedIngredients.length} ingredients failed to add. Check console for details. ⚠`);
    }
  }

  private parseIngredientText(ingredientText: string): { quantity: number; unit: string; name: string } {
    // Simple parsing logic - you can make this more sophisticated
    const text = ingredientText.trim();

    // Try to extract quantity and unit from start of string
    const quantityMatch = text.match(/^(\d+(?:\.\d+)?(?:\/\d+)?)\s*([a-zA-Z]*)\s+(.+)$/);

    if (quantityMatch) {
      const [, quantityStr, unit, name] = quantityMatch;

      // Convert fractions like "1/2" to decimals
      let quantity = 1;
      if (quantityStr.includes('/')) {
        const [num, den] = quantityStr.split('/');
        quantity = parseFloat(num) / parseFloat(den);
      } else {
        quantity = parseFloat(quantityStr);
      }

      return {
        quantity: quantity,
        unit: unit || 'piece', // Default unit
        name: name.trim()
      };
    }

    // If no quantity detected, assume 1 piece
    return {
      quantity: 1,
      unit: 'piece',
      name: text
    };
  }

  // private async addRecipe() {
  //   const addRecipeImg = getElementById("addRecipeImg") as HTMLImageElement;
  //   addRecipeImg.src = "../img/bars-loading.svg";
  //   const recipeName = this.newRecipeOptions.name;
  //   const defaultPeople = this.newRecipeOptions.default_people;

  //   if (!recipeName || !defaultPeople) {
  //     this.message.timedMessage("⚠ Recipe Name & Default People cannot be empty! ⚠");
  //     console.log("⚠ Recipe Name & Default People cannot be empty! ⚠")
  //     addRecipeImg.src = "../assets/plus.svg";
  //     return
  //   }

  //   try {
  //     await this.recipeStore.addRecipe(recipeName, defaultPeople);
  //     this.message.timedMessage(`Successfully added '${recipeName}'`);
  //     addRecipeImg.src = "../assets/plus.svg";
  //     reloadWindow();
  //     return `successfully added '${recipeName}'`;
  //   }
  //   catch (e: any) {
  //     console.log(e);
  //     this.message.timedMessage(`⚠ Unexpected error adding '${recipeName}' ⚠`);
  //     addRecipeImg.src = "../assets/plus.svg";
  //     throw new Error(e);
  //   }
  // }

  // private async addRecipeIngredient(recipe: IRecipe) {
  //   const addRecipeIngredientImg = getElementById("addRecipeIngredientImg") as HTMLImageElement;
  //   addRecipeIngredientImg.src = "../img/pizza-0.5s-182px.svg";
  //   const ingredientName = this.newRecipeIngredientOptions.ingredient_name;
  //   const unitName = this.newRecipeIngredientOptions.unit_name;
  //   const quantity = this.newRecipeIngredientOptions.quantity;

  //   if (!ingredientName || !unitName || !quantity) {
  //     this.message.timedMessage("⚠ Ingredient, Unit & Quantity cannot be empty! ⚠");
  //     console.log("⚠ Ingredient, Unit & Quantity cannot be empty! ⚠")
  //     addRecipeIngredientImg.src = "/img/plus.svg";
  //     return
  //   }

  //   try {
  //     await postApi(apiUrl, "recipeingredient", {
  //       recipe_name: recipe.name,
  //       ingredient_name: ingredientName,
  //       quantity_per_person: quantity / recipe.default_people,
  //       unit_name: unitName,
  //     }, apiToken);
  //     console.log(`successfully added '${ingredientName}'`);
  //     this.getRecipeIngredients(recipe);
  //   } catch (e: any) {
  //     console.log(`error adding ${ingredientName}`);
  //     console.log(e);
  //     this.message.timedMessage(`⚠ Unexpected error adding '${ingredientName}'! ⚠`);
  //   }

  //   addRecipeIngredientImg.src = "/img/plus.svg";
  // }

  private async deleteRecipeIngredient(recipe: IRecipe, ingredientName: string) {
    try {
      await deleteApi(apiUrl, "recipeingredient", undefined, apiToken, {
        recipe_name: recipe.name,
        ingredient_name: ingredientName,
      });
      console.log(`successfully deleted ${ingredientName}`);
      this.getRecipeIngredients(recipe);
    } catch (e: any) {
      console.log(`error deleting ${ingredientName}`);
      console.log(e);
      this.message.timedMessage(`⚠ Unexpected error deleting '${ingredientName}'! ⚠`)
    }
  }

  private async loadRecipeEditSection(): Promise<void> {
    getElementById("recipe_edit_loading").classList.add("hidden");

    const recipeEditContentDiv = getElementById("recipe_edit_content")
    recipeEditContentDiv.classList.remove("hidden");

    const householdRecipesTemplate = document.querySelector("[data-for-each=householdRecipes]") as HTMLElement;
    try {
      console.log("Loading recipes, total:", this.recipeStore.householdRecipes.length);
      console.log("Empty recipes array:", this.recipeStore.emptyRecipes);
      console.log("Empty recipes count:", this.recipeStore.emptyRecipes?.length || 0);

      if (this.recipeStore.emptyRecipes && this.recipeStore.emptyRecipes.length > 0) {
        console.log("Empty recipe names:", this.recipeStore.emptyRecipes.map((r: IRecipe) => r.name));
      }

      (this.recipeStore.householdRecipes.sort((a, b) => a.name.localeCompare(b.name))).forEach((recipe: any) => {
        const recipeElement = householdRecipesTemplate?.cloneNode(true) as HTMLElement;
        const recipeButton = recipeElement.querySelector("button") as HTMLButtonElement;
        recipeButton.textContent = recipe.name;

        // Check if recipe is in the empty recipes list (with null safety)
        const isEmpty = this.recipeStore.emptyRecipes && Array.isArray(this.recipeStore.emptyRecipes)
          ? this.recipeStore.emptyRecipes.some((emptyRecipe: IRecipe) => emptyRecipe.name === recipe.name)
          : false;

        if (isEmpty) {
          console.log(`Recipe "${recipe.name}" is EMPTY - highlighting in red`);
        }

        // Apply red text color if recipe is empty
        if (isEmpty) {
          recipeButton.className = "w-full text-left px-3 py-2 hover:bg-gray-700 transition-colors rounded text-red-400";
        } else {
          recipeButton.className = "w-full text-left px-3 py-2 hover:bg-gray-700 transition-colors rounded text-white";
        }

        recipeButton.addEventListener("click", () => {
          this.selectRecipe(recipe.name);
        });
        recipeEditContentDiv.appendChild(recipeElement!);
        householdRecipesTemplate.remove();
      });

      console.log("Recipes loaded successfully");
    }
    catch (error) {
      console.error("Error loading recipe edit section:", error);
      setError("recipe_edit");
    }
  }

  // private async getEmptyRecipes() {
  //   getElementById("empty_recipes_content").classList.add("hidden");
  //   getElementById("empty_recipes_refresh").classList.add("hidden");
  //   getElementById("empty_recipes_loading").classList.remove("hidden");

  //   const emptyRecipesTemplate = document.querySelector("[data-for-each=emptyRecipes]") as HTMLElement;
  //   emptyRecipesTemplate.classList.remove("hidden");
  //   try {
  //     await this.recipeStore.getEmptyRecipes();
  //     this.recipeStore.emptyRecipes.forEach((recipe: IRecipe) => {
  //       const emptyRecipe = emptyRecipesTemplate.cloneNode(true) as HTMLElement;
  //       const emptyRecipeButton = emptyRecipe.querySelector("button") as HTMLButtonElement;
  //       emptyRecipeButton.textContent = recipe.name;
  //       emptyRecipeButton.addEventListener("click", () => {
  //         getElementById("recipe_edit_hideable").classList.remove("hidden");
  //         getElementById("empty_recipes_hideable").classList.add("hidden");
  //         this.selectRecipe(recipe.name);
  //       });
  //       emptyRecipesTemplate.parentElement!.appendChild(emptyRecipe);
  //     });
  //     emptyRecipesTemplate.classList.add("hidden");
  //   } catch (e: any) {
  //     console.log(e);
  //     this.message.timedMessage(`⚠ Unexpected error getting empty recipes! ⚠`)
  //   }
  //   getElementById("empty_recipes_content").classList.remove("hidden");
  //   getElementById("empty_recipes_refresh").classList.remove("hidden");
  //   getElementById("empty_recipes_loading").classList.add("hidden");
  // }

  private async showRecipeIngredientsModal(recipe: any): Promise<void> {
    // Remove any existing modal first
    const existingModal = document.getElementById('recipe-ingredients-modal');
    if (existingModal) {
      existingModal.remove();
    }

    // Create modal overlay
    const modalOverlay = document.createElement('div');
    modalOverlay.id = 'recipe-ingredients-modal';
    modalOverlay.className = 'fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4';

    const modalHTML = `
      <div class="bg-gray-800 rounded-lg shadow-2xl max-w-3xl max-h-[95vh] overflow-hidden m-auto w-full flex flex-col text-white border border-gray-700">
        <div class="p-4 border-b border-gray-700 bg-gray-800">
          <div class="flex justify-between items-center">
            <h2 class="text-xl font-bold text-white">${recipe.name}</h2>
            <button id="close-ingredients-modal" class="text-gray-400 hover:text-white text-3xl leading-none">&times;</button>
          </div>
        </div>

        <div class="flex-1 overflow-y-auto p-4 space-y-4">
          <!-- Recipe Book Info -->
          <div class="bg-gray-700 p-3 rounded-lg">
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-xs font-medium text-gray-300 mb-1">Recipe Book</label>
                <div class="flex items-center gap-2">
                  <span id="modal-book-name" class="text-white">${recipe.recipe_book?.name || 'No Recipe Book'}</span>
                  <button id="modal-edit-book-btn" data-toggled="false" class="text-gray-400 hover:text-white">
                    <img id="modal-book-img" src="/img/edit.svg" alt="Edit" width="16" height="16" style="filter: brightness(0) invert(1)" />
                  </button>
                </div>
              </div>
              <div>
                <label class="block text-xs font-medium text-gray-300 mb-1">Page Number</label>
                <div class="flex items-center gap-2">
                  <span id="modal-book-page" class="text-white">${recipe.recipe_book_page ? `Page ${recipe.recipe_book_page}` : 'No Page'}</span>
                  <button id="modal-edit-page-btn" data-toggled="false" class="text-gray-400 hover:text-white">
                    <img id="modal-page-img" src="/img/edit.svg" alt="Edit" width="16" height="16" style="filter: brightness(0) invert(1)" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Add Ingredient Form -->
          <div class="bg-gray-700 p-3 rounded-lg">
            <h3 class="text-sm font-semibold text-white mb-3">Add Ingredient</h3>
            <div class="grid grid-cols-1 md:grid-cols-4 gap-2">
              <div>
                <input type="text" id="modal-ingredient-name" class="w-full px-2 py-1.5 border border-gray-500 rounded bg-gray-600 text-white text-sm" list="modal-ingredients-list" placeholder="Ingredient" />
                <datalist id="modal-ingredients-list"></datalist>
              </div>
              <div>
                <select id="modal-ingredient-unit" class="w-full px-2 py-1.5 border border-gray-500 rounded bg-gray-600 text-white text-sm">
                  <option value="">Unit</option>
                </select>
              </div>
              <div>
                <input type="number" id="modal-ingredient-quantity" class="w-full px-2 py-1.5 border border-gray-500 rounded bg-gray-600 text-white text-sm" min="1" placeholder="Amount (${recipe.default_people} ${recipe.default_people == 1 ? 'person' : 'people'})" />
              </div>
              <div>
                <button id="modal-add-ingredient-btn" class="w-full px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium">
                  Add
                </button>
              </div>
            </div>
          </div>

          <!-- Ingredients List -->
          <div class="bg-gray-700 p-3 rounded-lg">
            <h3 class="text-sm font-semibold text-white mb-3">Ingredients (${recipe.default_people} ${recipe.default_people == 1 ? 'person' : 'people'})</h3>
            <div id="modal-ingredients-loading">
              <img alt="Loading" src="/img/pizza-0.5s-182px.svg" class="w-16 mx-auto" />
              <p class="text-center text-gray-400">Loading ingredients...</p>
            </div>
            <div id="modal-ingredients-error" class="hidden">
              <img class="w-16 mx-auto" alt="Error" src="/img/exclamation.svg" />
              <p class="text-center text-red-400">Error loading ingredients</p>
            </div>
            <div id="modal-ingredients-list-content" class="hidden space-y-2">
              <div id="modal-no-ingredients" class="hidden text-center text-gray-400 italic p-3 border-2 border-dashed border-gray-600 rounded-lg text-sm">
                No ingredients found. Add ingredients above.
              </div>
              <div id="modal-ingredients-container"></div>
            </div>
          </div>
        </div>

        <div class="p-4 border-t border-gray-700 bg-gray-800">
          <button id="close-ingredients-modal-btn" class="px-4 py-2 text-sm text-gray-300 hover:text-white border border-gray-600 rounded hover:bg-gray-700 transition-colors">
            Close
          </button>
        </div>
      </div>
    `;

    modalOverlay.innerHTML = modalHTML;
    document.body.appendChild(modalOverlay);

    // Setup modal event listeners
    await this.setupIngredientsModalListeners(modalOverlay, recipe);
  }

  private async setupIngredientsModalListeners(modalOverlay: HTMLElement, recipe: any): Promise<void> {
    // Close modal handlers
    const closeModal = () => modalOverlay.remove();
    getElementById('close-ingredients-modal').addEventListener('click', closeModal);
    getElementById('close-ingredients-modal-btn').addEventListener('click', closeModal);
    modalOverlay.addEventListener('click', (e) => {
      if (e.target === modalOverlay) closeModal();
    });

    // Populate ingredients and units
    const ingredientDatalist = getElementById('modal-ingredients-list');
    this.ingredientStore.ingredients.forEach((ingredient: any) => {
      const option = document.createElement('option');
      option.value = ingredient.name;
      ingredientDatalist.appendChild(option);
    });

    const unitSelect = getElementById('modal-ingredient-unit') as HTMLSelectElement;
    this.unitStore.units.forEach((unit: any) => {
      const option = document.createElement('option');
      option.value = unit.name;
      option.textContent = unit.name;
      unitSelect.appendChild(option);
    });

    // Add ingredient button
    getElementById('modal-add-ingredient-btn').addEventListener('click', async () => {
      const ingredientName = (getElementById('modal-ingredient-name') as HTMLInputElement).value.trim();
      const unitName = (getElementById('modal-ingredient-unit') as HTMLSelectElement).value;
      const quantity = parseFloat((getElementById('modal-ingredient-quantity') as HTMLInputElement).value);

      if (!ingredientName || !unitName || !quantity) {
        this.message.timedMessage('⚠ Ingredient, Unit & Quantity cannot be empty! ⚠');
        return;
      }

      const addButton = getElementById('modal-add-ingredient-btn') as HTMLButtonElement;
      addButton.disabled = true;
      addButton.textContent = 'Adding...';

      try {
        await postApi(apiUrl, "recipeingredient", {
          recipe_name: recipe.name,
          ingredient_name: ingredientName,
          quantity_per_person: quantity / recipe.default_people,
          unit_name: unitName,
        }, apiToken);

        // Clear inputs
        (getElementById('modal-ingredient-name') as HTMLInputElement).value = '';
        (getElementById('modal-ingredient-unit') as HTMLSelectElement).value = '';
        (getElementById('modal-ingredient-quantity') as HTMLInputElement).value = '';

        // Reload ingredients
        await this.loadModalRecipeIngredients(recipe);
      } catch (error) {
        console.error('Error adding ingredient:', error);
        this.message.timedMessage('⚠ Error adding ingredient! ⚠');
      } finally {
        addButton.disabled = false;
        addButton.textContent = 'Add';
      }
    });

    // Edit book button
    getElementById('modal-edit-book-btn').addEventListener('click', async () => {
      const button = getElementById('modal-edit-book-btn') as HTMLButtonElement;
      const bookNameSpan = getElementById('modal-book-name');
      const img = getElementById('modal-book-img') as HTMLImageElement;

      if (button.dataset.toggled === 'false') {
        // Show select
        button.dataset.toggled = 'true';
        img.src = cross;

        const select = document.createElement('select');
        select.id = 'modal-book-select';
        select.className = 'border border-gray-200 rounded px-1 bg-gray-600 text-white';

        const nullOption = document.createElement('option');
        nullOption.value = '';
        nullOption.textContent = '-- None --';
        select.appendChild(nullOption);

        this.recipeBookStore.recipe_books.sort((a: any, b: any) => a.name.localeCompare(b.name)).forEach((book: IRecipeBook) => {
          const option = document.createElement('option');
          option.value = book.id.toString();
          option.textContent = book.name;
          option.dataset.id = book.id.toString();
          option.dataset.name = book.name;
          option.dataset.author = book.author;
          if (recipe.recipe_book && book.id === recipe.recipe_book.id) {
            option.selected = true;
          }
          select.appendChild(option);
        });

        bookNameSpan.innerHTML = '';
        bookNameSpan.appendChild(select);

        select.addEventListener('change', async (e: any) => {
          button.dataset.toggled = 'false';
          img.src = edit;

          const selectedOption = e.target.selectedOptions[0];
          let bookName = selectedOption.textContent;

          if (selectedOption.value === '') {
            await this.recipeStore.setRecipeBook(recipe, undefined);
            recipe.recipe_book = undefined;
            bookName = 'No Recipe Book';
          } else {
            const bookData = {
              id: selectedOption.dataset.id,
              name: selectedOption.dataset.name,
              author: selectedOption.dataset.author
            };
            await this.recipeStore.setRecipeBook(recipe, bookData);
            recipe.recipe_book = bookData;
          }

          bookNameSpan.innerHTML = bookName;
        });
      } else {
        // Cancel edit
        button.dataset.toggled = 'false';
        img.src = edit;
        bookNameSpan.innerHTML = recipe.recipe_book?.name || 'No Recipe Book';
      }
    });

    // Edit page button
    getElementById('modal-edit-page-btn').addEventListener('click', async () => {
      const button = getElementById('modal-edit-page-btn') as HTMLButtonElement;
      const pageSpan = getElementById('modal-book-page');
      const img = getElementById('modal-page-img') as HTMLImageElement;

      if (button.dataset.toggled === 'false') {
        // Show input
        button.dataset.toggled = 'true';
        img.src = tick;

        const input = document.createElement('input');
        input.id = 'modal-page-input';
        input.type = 'number';
        input.min = '0';
        input.value = recipe.recipe_book_page ? recipe.recipe_book_page.toString() : '';
        input.placeholder = 'Page Number';
        input.className = 'border border-gray-200 rounded px-2 py-1 bg-gray-600 text-white w-24';

        pageSpan.innerHTML = '';
        pageSpan.appendChild(input);
        input.focus();
      } else {
        // Save page
        button.dataset.toggled = 'false';
        img.src = edit;

        const input = getElementById('modal-page-input') as HTMLInputElement;
        const pageValue = input.value;

        if (pageValue === '' || pageValue === '0') {
          await this.recipeStore.setRecipeBookPage(recipe, undefined);
          recipe.recipe_book_page = undefined;
          pageSpan.innerHTML = 'No Page';
        } else {
          const pageNum = parseInt(pageValue);
          await this.recipeStore.setRecipeBookPage(recipe, pageNum);
          recipe.recipe_book_page = pageNum;
          pageSpan.innerHTML = `Page ${pageNum}`;
        }
      }
    });

    // Load ingredients
    await this.loadModalRecipeIngredients(recipe);
  }

  private async loadModalRecipeIngredients(recipe: any): Promise<void> {
    getElementById('modal-ingredients-loading').classList.remove('hidden');
    getElementById('modal-ingredients-error').classList.add('hidden');
    getElementById('modal-ingredients-list-content').classList.add('hidden');

    try {
      const ingredients = await getApi(apiUrl, "recipeingredient", apiToken, { recipe_name: recipe.name });

      getElementById('modal-ingredients-loading').classList.add('hidden');
      getElementById('modal-ingredients-list-content').classList.remove('hidden');

      const container = getElementById('modal-ingredients-container');
      const noIngredientsMsg = getElementById('modal-no-ingredients');

      container.innerHTML = '';

      if (ingredients.length === 0) {
        noIngredientsMsg.classList.remove('hidden');
      } else {
        noIngredientsMsg.classList.add('hidden');

        ingredients.forEach((ingredient: any) => {
          const row = document.createElement('div');
          row.className = 'flex items-center justify-between p-2 bg-gray-600 rounded border border-gray-500';

          row.innerHTML = `
            <div class="flex-1">
              <span class="text-white font-medium">${ingredient.ingredient.name}</span>
            </div>
            <div class="flex-1 text-center">
              <span class="text-gray-300">${ingredient.quantity_per_person * recipe.default_people} ${ingredient.unit.suffix}</span>
            </div>
            <div>
              <button class="delete-ingredient text-red-400 hover:text-red-300 p-1 rounded hover:bg-gray-700" data-ingredient-name="${ingredient.ingredient.name}">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </button>
            </div>
          `;

          container.appendChild(row);
        });

        // Add delete listeners
        container.querySelectorAll('.delete-ingredient').forEach(btn => {
          btn.addEventListener('click', async (e: any) => {
            const ingredientName = e.currentTarget.dataset.ingredientName;
            try {
              await deleteApi(apiUrl, "recipeingredient", undefined, apiToken, {
                recipe_name: recipe.name,
                ingredient_name: ingredientName,
              });
              await this.loadModalRecipeIngredients(recipe);
            } catch (error) {
              console.error('Error deleting ingredient:', error);
              this.message.timedMessage('⚠ Error deleting ingredient! ⚠');
            }
          });
        });
      }
    } catch (error) {
      console.error('Error loading ingredients:', error);
      getElementById('modal-ingredients-loading').classList.add('hidden');
      getElementById('modal-ingredients-error').classList.remove('hidden');
    }
  }

  private async getRecipeIngredients(recipe: any): Promise<void> {
    // getElementById("recipe_edit_selectedRecipeIngredients_content").classList.add("hidden");
    // getElementById("recipe_edit_selectedRecipeIngredients_error").classList.add("hidden");
    // getElementById("recipe_edit_selectedRecipeIngredients_loading").classList.remove("hidden");
    setLoading("recipe_edit_selectedRecipeIngredients");

    const apiToken = getStringFromLocalStorage("idToken");
    try {
      const selectedRecipeIngredients = await getApi(apiUrl, "recipeingredient", apiToken, { recipe_name: recipe.name });
      this.selectedRecipeIngredients = selectedRecipeIngredients;
      saveJsonToLocalStorage("selectedRecipeIngredients", this.selectedRecipeIngredients);

      if (selectedRecipeIngredients.length === 0) {
        getElementById("noSelectedRecipeIngredients").classList.remove("hidden");
      } else {
        getElementById("noSelectedRecipeIngredients").classList.add("hidden");
      }

      const selectedRecipeIngredientTemplate = document.querySelector("[data-for-each=selectedRecipeIngredients]") as HTMLElement;
      selectedRecipeIngredientTemplate.classList.remove("hidden");
      document.querySelectorAll(".recipeIngredientElement").forEach((element: Element) => { element.remove() });
      this.selectedRecipeIngredients.forEach((recipeIngredient: any) => {
        const recipeIngredientElement = selectedRecipeIngredientTemplate.cloneNode(true) as HTMLElement;
        recipeIngredientElement.classList.add("recipeIngredientElement")
        const recipeIngredientName = recipeIngredientElement.querySelector("[data-key=ingredientName]") as HTMLElement;
        recipeIngredientName.textContent = recipeIngredient.ingredient.name;
        const recipeIngredientQuantity = recipeIngredientElement.querySelector("[data-key=ingredientQuantity]") as HTMLElement;
        recipeIngredientQuantity.textContent = `${recipeIngredient.quantity_per_person * recipe.default_people} ${recipeIngredient.unit.suffix}`
        const recipeIngredientDeleteButton = recipeIngredientElement.querySelector("[data-key=ingredientDelete]") as HTMLButtonElement;
        recipeIngredientDeleteButton.addEventListener("click", () => {
          this.deleteRecipeIngredient(recipe, recipeIngredient.ingredient.name);
        })
        selectedRecipeIngredientTemplate.parentElement?.appendChild(recipeIngredientElement);
      });
      selectedRecipeIngredientTemplate.classList.add("hidden")

      getElementById("recipe_edit_selectedRecipeIngredients_loading").classList.add("hidden");
      getElementById("recipe_edit_selectedRecipeIngredients_content").classList.remove("hidden");
    } catch {
      console.log("Error getting Recipe Ingredients...")
      getElementById("recipe_edit_selectedRecipeIngredients_loading").classList.add("hidden");
      getElementById("recipe_edit_selectedRecipeIngredients_error").classList.remove("hidden");
    }
  }

  private async selectRecipe(recipeName: string): Promise<string | void> {
    let recipe = this.recipeStore.householdRecipes.find((r: any) => r.name === recipeName);
    if (recipe) {
      // Show recipe ingredients in a modal
      await this.showRecipeIngredientsModal(recipe);
    } else {
      this.message.timedMessage('⚠ Recipe not found! ⚠');
    }
  }
}
